import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { message } from "antd";

// Define the Promotion interface based on the API response
export interface PromotionResponse {
  status: boolean;
  message: string;
  data: {
    id: number;
    name: string;
    description: string;
    banner: string;
  };
}

// Define the Promotion type for the form and mutation
export interface Promotion {
  id: number;
  name: string;
  description: string;
  banner: string;
}

// Fetch promotion data by ID
export const usePromotion = (promotionId: number) => {
  return useQuery<PromotionResponse>({
    queryKey: ["promotion", promotionId], // Include promotionId in the query key
    queryFn: async () => {
      const response = await apiClient.get<PromotionResponse>(
        `/promotion/${promotionId}`
      );
      return response.data; // Return the entire response data
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });
};

// Update promotion data
export const useUpdatePromotion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (promotionData: Promotion) => {
      const response = await apiClient.put(
        `/promotion/${promotionData.id}`,
        promotionData
      );
      return response.data;
    },
    onSuccess: () => {
      message.success("Promotion updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["promotion"] }); // Invalidate the promotion query cache
    },
    onError: () => {
      message.error("Failed to update promotion. Please try again.");
    },
  });
};
